import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type Analyse,
  type Einheit,
  type Einsatzabschnitt,
  type Posten,
  type PostenEingabe,
  type Stelle,
  type Veranstaltung,
} from '../api/client';
import { Schritte } from '../components/Schritte';
import { Annahme, Feld, Karte, Meldung, StatusPill, fehlerText } from '../components/ui';
import { MASSNAHME_TEXT, MATERIAL_TEXT, material, uhrzeit } from '../format';
import { link } from '../router';
import { stellenNummern } from './Planung';
import * as aktion from '../aktionen';
import { useUndo, type Schritt } from '../undo';

const FAHRZEUGE = [
  'ELW 1',
  'HLF 10',
  'HLF 20',
  'LF 10',
  'LF 20',
  'TSF',
  'TSF-W',
  'MLF',
  'MTF',
  'GW-L',
  'RW',
  'DLK 23-12',
  'KdoW',
];

/** Schritt 4: Einheiten und Abschnitte verwalten, Posten zuordnen (Drag & Drop oder Auswahl), Summen live. */
export function Kraefte({ id, darf }: { id: string; darf: boolean }) {
  const [v, setV] = useState<Veranstaltung | null>(null);
  const [analyse, setAnalyse] = useState<Analyse | null>(null);
  const [posten, setPosten] = useState<Posten[]>([]);
  const [einheiten, setEinheiten] = useState<Einheit[]>([]);
  const [abschnitte, setAbschnitte] = useState<Einsatzabschnitt[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [prueft, setPrueft] = useState(false);
  const pv = v?.planversion_id ?? null;
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const undo = useUndo();
  const stand = undo.stand;

  const neuPruefen = useCallback((planversion: string, sofort = false) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => {
        setPrueft(true);
        api
          .analysieren(planversion)
          .then((r) => setAnalyse(r.analyse))
          .catch((e: unknown) => setFehler(fehlerText(e)))
          .finally(() => setPrueft(false));
      },
      sofort ? 0 : 600,
    );
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const { veranstaltung } = await api.veranstaltung(id);
        setV(veranstaltung);
        const planversion = veranstaltung.planversion_id;
        if (!planversion) return;
        const [a, p, e, ab] = await Promise.all([
          api.analyse(planversion),
          api.posten(planversion),
          api.einheiten(),
          api.abschnitte(planversion),
        ]);
        setAnalyse(a.analyse);
        setPosten(p.posten);
        setEinheiten(e.einheiten);
        setAbschnitte(ab.einsatzabschnitte);
        if (stand > 0) neuPruefen(planversion, true); // nach Rückgängig/Wiederholen
      } catch (err) {
        setFehler(fehlerText(err));
      }
    })();
    return () => clearTimeout(timer.current);
  }, [id, stand, neuPruefen]);

  /** Jede Änderung als umkehrbarer Schritt; danach Daten neu laden und neu prüfen. */
  async function aenderung(s: Schritt) {
    if (!pv) return;
    setFehler(null);
    try {
      await undo.tue(s);
      const [p, e, ab] = await Promise.all([api.posten(pv), api.einheiten(), api.abschnitte(pv)]);
      setPosten(p.posten);
      setEinheiten(e.einheiten);
      setAbschnitte(ab.einsatzabschnitte);
      neuPruefen(pv);
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }

  if (!analyse) {
    return (
      <div className="seite">
        <Schritte id={id} aktiv="kraefte" titel={v?.titel} />
        {fehler ? (
          <Meldung art="fehler">{fehler}</Meldung>
        ) : (
          <Meldung art="info">
            Noch kein Abgleich vorhanden. Bitte zuerst unter{' '}
            <a href={link('v', id, 'planung')}>Route & Abgleich</a> die Route zeichnen.
          </Meldung>
        )}
      </div>
    );
  }

  const nummern = new Map(stellenNummern(analyse.stellen).map(([s, n]) => [s.id, n]));
  const stellen = analyse.stellen.filter((s) => s.massnahme !== 'polizei');
  const polizei = analyse.stellen.filter((s) => s.massnahme === 'polizei');
  const staerke = einheiten.reduce((n, e) => n + e.staerke, 0);
  const s = analyse.summen;

  return (
    <div className="seite">
      <Schritte id={id} aktiv="kraefte" titel={v?.titel} />
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      <div className={`banner ${analyse.vollstaendig ? 'banner--gruen' : ''}`} role="status">
        {analyse.vollstaendig ? (
          <>
            <StatusPill status="gruen" text="Plan vollständig" /> Keine roten oder offenen gelben
            Befunde. <a href={link('v', id, 'ausgabe')}>Weiter zur Ausgabe</a>
          </>
        ) : (
          <>
            <StatusPill status="rot" text={`${analyse.zaehler.rot} rot`} />{' '}
            <StatusPill status="gelb" text={`${analyse.zaehler.gelb_offen} gelb offen`} />{' '}
            {prueft
              ? 'wird neu geprüft …'
              : 'Rote Befunde (z. B. nicht angeordnete Kreuzungen) klärt die Behörde – siehe Route & Abgleich.'}
          </>
        )}
      </div>

      <div className="kraefte">
        <Karte titel={`Einheiten (${einheiten.length})`}>
          <p className="gedaempft">Auf einen Posten ziehen oder dort auswählen.</p>
          <ul className="einheiten">
            {einheiten.map((e) => (
              <li
                key={e.id}
                className="einheit"
                draggable={darf}
                onDragStart={(ev) => ev.dataTransfer.setData('text/plain', e.id)}
              >
                <strong>{e.name}</strong>
                <span>
                  {e.funkrufname ?? 'ohne Funkrufname'} · Stärke {e.staerke}
                  {e.fahrzeug && ` · ${e.fahrzeug}`}
                </span>
                <span className="gedaempft">
                  eingeplant an {posten.filter((p) => p.einheit_id === e.id).length} Posten
                </span>
              </li>
            ))}
          </ul>
          {darf && <EinheitFormular onAnlegen={(d) => void aenderung(aktion.einheitAnlegen(d))} />}
        </Karte>

        <Karte titel="Stellen und Posten">
          {stellen.map((st) => (
            <StellenKarte
              key={st.id}
              s={st}
              nr={nummern.get(st.id) ?? 0}
              posten={posten.filter((p) => p.sperrstelle_id === st.id)}
              einheiten={einheiten}
              abschnitte={abschnitte}
              darf={darf}
              vorschlag={analyse.kraefte_vorschlag[st.id]}
              stellenName={(sid) => analyse.stellen.find((x) => x.id === sid)?.bezeichnung ?? sid}
              onUebernehmen={() => pv && void aenderung(aktion.vorschlagUebernehmen(pv, st.id))}
              onNeu={() =>
                void aenderung(
                  aktion.postenAnlegen(st.id, {
                    einheit_id: null,
                    einsatzabschnitt_id: null,
                    personen: 1,
                    auftrag: null,
                  }),
                )
              }
              onAendern={(p, d) =>
                void aenderung(
                  aktion.postenAendern(
                    p,
                    d,
                    d.einheit_id !== p.einheit_id ? 'Einheit zugeordnet' : 'Posten geändert',
                  ),
                )
              }
              onLoeschen={(p) => void aenderung(aktion.postenLoeschen(p))}
            />
          ))}
          {polizei.length > 0 && (
            <p className="gedaempft">
              Durch Polizei gesichert (keine eigenen Kräfte):{' '}
              {polizei.map((p) => `${p.kennung ?? ''} ${p.bezeichnung}`).join(', ')}
            </p>
          )}
        </Karte>

        <div className="kraefte__rechts">
          <Karte titel="Summen">
            <dl className="summen">
              <dt>Mindestbedarf gleichzeitig</dt>
              <dd>{s.personal_mindestens} Personen</dd>
              <dt>Mit Wegezeiten (mitwandernd)</dt>
              <dd>{s.personal_mit_wegen} Personen</dd>
              <dt>Eingeplant an Posten</dt>
              <dd>{s.personen_geplant} Personen</dd>
              <dt>Stärke aller Einheiten</dt>
              <dd className={staerke < s.personal_mindestens ? 'knapp' : ''}>{staerke} Personen</dd>
              <dt>Fahrzeuge als Sperre</dt>
              <dd>{s.fahrzeuge}</dd>
            </dl>
            <table className="tabelle tabelle--kompakt">
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Bedarf</th>
                  <th>Bestand</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(s.material_bedarf).map(([art, n]) => (
                  <tr key={art} className={n > (s.material_bestand[art] ?? 0) ? 'knapp' : ''}>
                    <td>{MATERIAL_TEXT[art] ?? art}</td>
                    <td>{n}</td>
                    <td>{s.material_bestand[art] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="gedaempft">
              <Annahme>Bedarf je Stelle aus dem Regelwerk</Annahme> – Bestand unter Einstellungen
              pflegen.
            </p>
          </Karte>
          <Abschnitte
            pv={pv}
            abschnitte={abschnitte}
            darf={darf}
            onSchritt={(s) => void aenderung(s)}
            posten={posten}
          />
        </div>
      </div>
    </div>
  );
}

function StellenKarte(props: {
  s: Stelle;
  nr: number;
  posten: Posten[];
  einheiten: Einheit[];
  abschnitte: Einsatzabschnitt[];
  darf: boolean;
  vorschlag?: { von_stelle: string | null; personen: number }[];
  stellenName: (id: string) => string;
  onUebernehmen: () => void;
  onNeu: () => void;
  onAendern: (p: Posten, d: PostenEingabe) => void;
  onLoeschen: (p: Posten) => void;
}) {
  const { s, posten, darf } = props;
  const herkunft = (props.vorschlag ?? []).filter((x) => x.von_stelle !== null);
  return (
    <article className={`stellenkarte stellenkarte--${s.status}`}>
      <header>
        <span className="stelle__nr">
          {s.kennung && s.herkunft !== 'erkannt' ? s.kennung : props.nr}
        </span>
        <div>
          <strong>{s.bezeichnung}</strong>
          <div className="gedaempft">
            {s.herkunft === 'erkannt'
              ? 'Nicht angeordnet'
              : s.massnahme
                ? MASSNAHME_TEXT[s.massnahme]
                : ''}
            {s.zeiten &&
              ` · besetzen ab ca. ${uhrzeit(s.zeiten.besetzen)}, Freigabe ca. ${uhrzeit(s.zeiten.freigabe)}`}
          </div>
        </div>
        <StatusPill status={s.status} />
      </header>
      {s.bedarf && (
        <p className="vorschlag">
          <strong>
            {s.vorschlag === 'teilnehmer_sichern'
              ? 'Vorschlag (ohne Verkehrsregelung): '
              : 'Vorschlag: '}
          </strong>
          {s.bedarf.personen} {s.bedarf.personen === 1 ? 'Person' : 'Personen'}
          {s.bedarf.fahrzeug && ', Fahrzeug als Sperre'}
          {Object.keys(s.bedarf.material).length > 0 && `, ${material(s.bedarf.material)}`}.{' '}
          <em>{s.bedarf.begruendung}</em>
          {herkunft.length > 0 && (
            <span className="gedaempft">
              {' '}
              Kräfte können von{' '}
              {herkunft
                .map((h) => `„${props.stellenName(h.von_stelle!)}“ (${h.personen})`)
                .join(', ')}{' '}
              nachrücken.
            </span>
          )}
        </p>
      )}
      {s.befunde
        .filter((b) =>
          [
            'UNBESETZT',
            'UNTERBESETZT',
            'OHNE_EINHEIT',
            'NICHT_ANGEORDNET',
            'ZEITKONFLIKT',
          ].includes(b.code),
        )
        .map((b) => (
          <p key={b.schluessel} className={`befund befund--${b.status}`}>
            {b.begruendung}
          </p>
        ))}
      {posten.map((p) => (
        <PostenZeile key={p.id} p={p} {...props} />
      ))}
      {darf && (
        <div className="knopfreihe">
          {posten.length === 0 && s.bedarf && s.bedarf.personen > 0 && (
            <button type="button" className="knopf knopf--primaer" onClick={props.onUebernehmen}>
              Vorschlag übernehmen
            </button>
          )}
          <button type="button" className="knopf" onClick={props.onNeu}>
            Posten hinzufügen
          </button>
        </div>
      )}
    </article>
  );
}

function PostenZeile({
  p,
  einheiten,
  abschnitte,
  darf,
  onAendern,
  onLoeschen,
}: {
  p: Posten;
  einheiten: Einheit[];
  abschnitte: Einsatzabschnitt[];
  darf: boolean;
  onAendern: (p: Posten, d: PostenEingabe) => void;
  onLoeschen: (p: Posten) => void;
}) {
  const [auftrag, setAuftrag] = useState(p.auftrag ?? '');
  const [ueber, setUeber] = useState(false);
  useEffect(() => setAuftrag(p.auftrag ?? ''), [p.auftrag]);
  const basis: PostenEingabe = {
    einheit_id: p.einheit_id,
    einsatzabschnitt_id: p.einsatzabschnitt_id,
    personen: p.personen,
    auftrag: p.auftrag,
    material: p.material,
  };
  return (
    <div
      className={`posten${ueber ? ' posten--ziel' : ''}${p.einheit_id ? '' : ' posten--offen'}`}
      onDragOver={(e) => {
        if (!darf) return;
        e.preventDefault();
        setUeber(true);
      }}
      onDragLeave={() => setUeber(false)}
      onDrop={(e) => {
        e.preventDefault();
        setUeber(false);
        const eid = e.dataTransfer.getData('text/plain');
        if (eid) onAendern(p, { ...basis, einheit_id: eid });
      }}
    >
      <fieldset disabled={!darf} className="posten__felder">
        <Feld label="Einheit">
          <select
            value={p.einheit_id ?? ''}
            onChange={(e) => onAendern(p, { ...basis, einheit_id: e.target.value || null })}
          >
            <option value="">– noch keine (hierher ziehen) –</option>
            {einheiten.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
                {e.funkrufname ? ` (${e.funkrufname})` : ''}
              </option>
            ))}
          </select>
        </Feld>
        <Feld label="Personen">
          <input
            type="number"
            min={1}
            max={50}
            value={p.personen}
            onChange={(e) =>
              onAendern(p, { ...basis, personen: Math.max(1, Number(e.target.value)) })
            }
          />
        </Feld>
        <Feld label="Abschnitt">
          <select
            value={p.einsatzabschnitt_id ?? ''}
            onChange={(e) =>
              onAendern(p, { ...basis, einsatzabschnitt_id: e.target.value || null })
            }
          >
            <option value="">–</option>
            {abschnitte.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Feld>
        <Feld label="Auftrag">
          <textarea
            rows={2}
            value={auftrag}
            onChange={(e) => setAuftrag(e.target.value)}
            onBlur={() =>
              auftrag !== (p.auftrag ?? '') && onAendern(p, { ...basis, auftrag: auftrag || null })
            }
          />
        </Feld>
      </fieldset>
      <div className="posten__fuss">
        <span className="gedaempft">
          Material: {material(p.material)} · besetzen ab ca. {uhrzeit(p.besetzen_von)}
        </span>
        {darf && (
          <button
            type="button"
            className="knopf knopf--klein knopf--gefahr"
            onClick={() => onLoeschen(p)}
          >
            Posten entfernen
          </button>
        )}
      </div>
    </div>
  );
}

function EinheitFormular({ onAnlegen }: { onAnlegen: (d: Omit<Einheit, 'id'>) => void }) {
  const [name, setName] = useState('');
  const [funk, setFunk] = useState('');
  const [staerke, setStaerke] = useState(3);
  const [fahrzeug, setFahrzeug] = useState('');
  return (
    <details>
      <summary>Einheit hinzufügen</summary>
      <form
        className="formular"
        onSubmit={(e) => {
          e.preventDefault();
          onAnlegen({ name, funkrufname: funk || null, staerke, fahrzeug: fahrzeug || null });
          setName('');
          setFunk('');
        }}
      >
        <Feld label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="z. B. 1. Gruppe"
          />
        </Feld>
        <Feld label="Funkrufname">
          <input value={funk} onChange={(e) => setFunk(e.target.value)} />
        </Feld>
        <Feld label="Stärke (Personen)">
          <input
            type="number"
            min={1}
            max={99}
            value={staerke}
            onChange={(e) => setStaerke(Number(e.target.value))}
          />
        </Feld>
        <Feld label="Fahrzeug (Klartext)" hinweis="z. B. Löschgruppenfahrzeug LF 10">
          <input list="fahrzeuge" value={fahrzeug} onChange={(e) => setFahrzeug(e.target.value)} />
          <datalist id="fahrzeuge">
            {FAHRZEUGE.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </Feld>
        <button type="submit" className="knopf">
          Anlegen
        </button>
      </form>
    </details>
  );
}

function Abschnitte({
  pv,
  abschnitte,
  darf,
  onSchritt,
  posten,
}: {
  pv: string | null;
  abschnitte: Einsatzabschnitt[];
  darf: boolean;
  onSchritt: (s: Schritt) => void;
  posten: Posten[];
}) {
  const [name, setName] = useState('');
  const [leiter, setLeiter] = useState('');
  const [funk, setFunk] = useState('');
  const [gg, setGg] = useState('');
  return (
    <Karte titel={`Einsatzabschnitte (${abschnitte.length})`}>
      <ul className="einfache-liste">
        {abschnitte.map((a) => (
          <li key={a.id}>
            <strong>{a.name}</strong> – {a.leiter ?? 'Leitung offen'}
            {a.funkrufname && `, ${a.funkrufname}`}
            {a.gespraechsgruppe && `, GG ${a.gespraechsgruppe}`}
            {darf && pv && (
              <button
                type="button"
                className="knopf knopf--klein knopf--gefahr"
                onClick={() => onSchritt(aktion.abschnittLoeschen(pv, a, posten))}
              >
                Entfernen
              </button>
            )}
          </li>
        ))}
      </ul>
      {darf && pv && (
        <details>
          <summary>Abschnitt hinzufügen</summary>
          <form
            className="formular"
            onSubmit={(e) => {
              e.preventDefault();
              onSchritt(
                aktion.abschnittAnlegen(pv, {
                  name,
                  leiter: leiter || null,
                  funkrufname: funk || null,
                  gespraechsgruppe: gg || null,
                }),
              );
              setName('');
              setLeiter('');
            }}
          >
            <Feld label="Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="EA Nord"
              />
            </Feld>
            <Feld label="Abschnittsleitung">
              <input value={leiter} onChange={(e) => setLeiter(e.target.value)} />
            </Feld>
            <Feld label="Funkrufname">
              <input value={funk} onChange={(e) => setFunk(e.target.value)} />
            </Feld>
            <Feld label="Gesprächsgruppe">
              <input value={gg} onChange={(e) => setGg(e.target.value)} />
            </Feld>
            <button type="submit" className="knopf">
              Anlegen
            </button>
          </form>
        </details>
      )}
    </Karte>
  );
}
